/**
 * usePackingStations - Hook for managing packing station instances
 *
 * Provides CRUD operations for packing stations and user assignments.
 * Used by the production settings UI.
 */

import { trpc } from '@/lib/trpc';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';
import type {
  PackingRoutingRules,
  PackingStationResponse,
} from '@threadheads/shared';

export function usePackingStations() {
  const { selectedLocation } = useLocationContext();
  const locationId = selectedLocation?.id;
  const utils = trpc.useUtils();

  const query = trpc.stations.getPackingStations.useQuery(
    { locationId: locationId! },
    {
      enabled: !!locationId,
      staleTime: 30_000,
    }
  );

  const createMutation = trpc.stations.createPackingStation.useMutation({
    onSuccess: () => {
      utils.stations.getPackingStations.invalidate();
    },
  });

  const updateMutation = trpc.stations.updatePackingStation.useMutation({
    onSuccess: () => {
      utils.stations.getPackingStations.invalidate();
    },
  });

  const deleteMutation = trpc.stations.deletePackingStation.useMutation({
    onSuccess: () => {
      utils.stations.getPackingStations.invalidate();
    },
  });

  const assignMutation = trpc.stations.assignUserToStation.useMutation({
    onSuccess: () => {
      utils.stations.getPackingStations.invalidate();
    },
  });

  const unassignMutation = trpc.stations.unassignUser.useMutation({
    onSuccess: () => {
      utils.stations.getPackingStations.invalidate();
    },
  });

  return {
    stations: (query.data ?? []) as PackingStationResponse[],
    isLoading: query.isLoading,
    error: query.error,

    createStation: async (name: string, routing?: PackingRoutingRules) => {
      if (!locationId) throw new Error('No location selected');
      return createMutation.mutateAsync({ locationId, name, routing });
    },
    isCreating: createMutation.isPending,

    updateStation: async (stationId: string, updates: {
      name?: string;
      routing?: PackingRoutingRules;
      isActive?: boolean;
      skipVerification?: boolean;
    }) => {
      return updateMutation.mutateAsync({ stationId, ...updates });
    },
    isUpdating: updateMutation.isPending,

    deleteStation: async (stationId: string) => {
      return deleteMutation.mutateAsync({ stationId });
    },
    isDeleting: deleteMutation.isPending,

    assignUser: async (stationId: string, profileId: string) => {
      return assignMutation.mutateAsync({ stationId, profileId });
    },
    isAssigning: assignMutation.isPending,

    unassignUser: async (profileId: string) => {
      return unassignMutation.mutateAsync({ profileId });
    },
    isUnassigning: unassignMutation.isPending,
  };
}
